// src/App.js
import React, { useState, useEffect, Suspense } from 'react';
import Login from './components/auth/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EmployeeModalProvider } from './contexts/EmployeeModalContext';
import { ProjectModalProvider } from './contexts/ProjectModalContext';
import { ClientModalProvider } from './contexts/ClientModalContext';
import { OpportunityModalProvider } from './contexts/OpportunityModalContext';
import { setAuthContext } from './utils/api';
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  Snackbar,
  Alert,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import useCardGrid from './hooks/useCardGrid';
import logger from './utils/logger';
import ErrorBoundary from './components/common/ErrorBoundary';
// Import chartConfig to register Chart.js components before any charts render
import './utils/chartConfig';

// Layout and core components (keep these as regular imports for critical path)
import MainLayout from './components/layout/MainLayout';

// Lazy load heavy components
const Concierge = React.lazy(() => import('./components/concierge/Concierge'));
const MatchingWizard = React.lazy(() => import('./components/matching/MatchingWizard'));
// Lazy load card components
const EmployeeCard = React.lazy(() => import('./components/cards/EmployeeCard'));
const ProjectCard = React.lazy(() => import('./components/cards/ProjectCard'));
const ClientCard = React.lazy(() => import('./components/cards/ClientCard'));
const ProcessCard = React.lazy(() => import('./components/cards/ProcessCard'));
const OpportunityCard = React.lazy(() => import('./components/cards/OpportunityCard'));

// Create dark theme with responsive breakpoints for iPad
const darkTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      // Custom breakpoints for tablets
      tablet: 768,
      ipad: 1024
    }
  },
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1E1E1E'
    },
    primary: {
      main: '#BB86FC',
      light: '#CF6679'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255,255,255,0.7)'
    }
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    // Responsive font sizes - more compact on tablets
    fontSize: 14, // Base font size
    h1: {
      '@media (max-width:1024px)': {
        fontSize: '1.75rem'
      }
    },
    h2: {
      '@media (max-width:1024px)': {
        fontSize: '1.5rem'
      }
    },
    h3: {
      '@media (max-width:1024px)': {
        fontSize: '1.25rem'
      }
    },
    h4: {
      '@media (max-width:1024px)': {
        fontSize: '1.1rem'
      }
    },
    h5: {
      '@media (max-width:1024px)': {
        fontSize: '1rem'
      }
    },
    h6: {
      '@media (max-width:1024px)': {
        fontSize: '0.9rem'
      }
    },
    body1: {
      '@media (max-width:1024px)': {
        fontSize: '0.8rem'
      }
    },
    body2: {
      '@media (max-width:1024px)': {
        fontSize: '0.7rem'
      }
    },
    caption: {
      '@media (max-width:1024px)': {
        fontSize: '0.65rem'
      }
    },
    button: {
      '@media (max-width:1024px)': {
        fontSize: '0.8rem'
      }
    }
  },
  components: {
    // Increase touch target sizes for mobile/tablet but reduce padding
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width:1024px)': {
            padding: '8px' // Smaller padding on tablets
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width:1024px)': {
            minHeight: '40px',
            minWidth: '40px',
            fontSize: '0.8rem',
            padding: '6px 12px'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:1024px)': {
            fontSize: '0.7rem',
            height: '24px'
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          '@media (max-width:1024px)': {
            fontSize: '0.7rem'
          }
        }
      }
    }
  }
});

// Enable debug mode with environmental variable (currently unused but kept for future use)
// const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === 'true';

/**
 * Inner App Component that uses authentication context.
 * Separated from main App to allow use of useAuth hook.
 */
function AppContent() {
  const { isAuthenticated, loading, logout: authLogout } = useAuth();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State for UI feedback
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  // Use the card grid hook to manage card state and positioning
  const { 
    cards,
    addCard,
    moveCardToFront,
    moveCardToColumn,
    removeCard,
    clearGrid,
    restoreCards,
    getTotalCardCount
  } = useCardGrid();

  /**
   * Handle content change requests from Concierge component.
   * 
   * This function processes requests to display new content cards from the
   * Concierge AI assistant. It validates the content, checks against the
   * maximum card limit, and adds the card to the grid if valid.
   * 
   * @param {Object} contentToRender - Content to render in a card
   * @param {string} contentToRender.type - Type of content (employee, project, client, etc.)
   * @param {string} [contentToRender.cardId] - Optional ID for the card
   * @param {Object} contentToRender[type] - The content data specific to the type
   */
  const handleContentChange = (contentToRender) => {
    if (!contentToRender || !contentToRender.type) {
      logger.warn('Invalid content change request:', contentToRender);
      return;
    }

    // Check if we're at the max card limit
    const totalCards = getTotalCardCount();
    if (totalCards >= 15) {
      logger.warn('Maximum card limit (15) reached. Remove some cards to add more.');
      setShowLimitAlert(true);
      return;
    }

    const { type, cardId } = contentToRender;
    // Use the cardId provided by ConversationPanel, or generate one if not provided
    const finalCardId = cardId || `${type}_${Date.now()}`;

    // Add the card to our grid
    addCard(finalCardId, contentToRender[type], type);
    logger.debug(`Created new ${type} card with ID: ${finalCardId}`);
  };

  /**
   * Handle card position changes from dragging.
   * 
   * This function updates a card's position in the grid when the user
   * drags and drops it. It logs position changes for debugging and
   * ensures the card exists before attempting to move it.
   * 
   * @param {string} cardId - ID of the card being moved
   * @param {Object} newPosition - New position coordinates
   * @param {number} newPosition.x - X coordinate
   * @param {number} newPosition.y - Y coordinate
   */
  const handlePositionChange = (cardId, newPosition) => {
    logger.debug('[APP] Position change for card:', cardId, newPosition);

    // Ensure the card exists before trying to move it
    if (!cards[cardId]) {
      logger.warn('[APP] Card not found in handlePositionChange:', cardId);
      return;
    }

    try {
      logger.debug('[APP] Calling moveCardToColumn directly');
      moveCardToColumn(cardId, newPosition);

      // Log the card's position after the move
      setTimeout(() => {
        const card = cards[cardId];
        if (card) {
          logger.debug('[APP] Card position after move:', card.gridPosition);
        }
      }, 200);
    } catch (error) {
      logger.error('[APP] Error in handlePositionChange:', error);
    }
  };
  
  /**
   * Render all active cards in the grid.
   * 
   * This function maps through all card objects and renders the appropriate
   * card component based on the card type. It passes common props to all cards
   * and specific data to each card type.
   * 
   * @returns {React.Component[]} Array of rendered card components
   */
  const renderCards = () => {
    return Object.keys(cards).map(cardId => {
      const card = cards[cardId];
      
      // Common props for all card types
      const commonProps = {
        key: cardId,
        id: cardId,
        onClose: () => {
          logger.debug('Card close handler called for cardId:', cardId);

          // First remove the card from the grid
          removeCard(cardId);

          // Then notify the ConversationPanel to remove it from selectedItems
          if (window.handleCardRemove) {
            logger.debug('Calling window.handleCardRemove with cardId:', cardId);
            window.handleCardRemove(cardId);
          } else {
            logger.warn('window.handleCardRemove is not defined');
          }
        },
        zIndex: card.zIndex,
        position: { x: card.x, y: card.y },
        onPositionChange: (newPos) => handlePositionChange(cardId, newPos),
        onClick: () => moveCardToFront(cardId),
        stackPosition: parseInt(card.gridPosition.charAt(1), 10),
        style: {},
        cardType: card.type
      };
      
      // Extract the key prop from commonProps
      const { key, ...restProps } = commonProps;
      
      // Render appropriate card type with key passed directly
      switch (card.type) {
        case 'employee': return <EmployeeCard key={key} {...restProps} employee={card.data} />;
        case 'project': return <ProjectCard key={key} {...restProps} project={card.data} />;
        case 'client': return <ClientCard key={key} {...restProps} client={card.data} />;
        case 'process': return <ProcessCard key={key} {...restProps} process={card.data} />;
        case 'opportunity': return <OpportunityCard key={key} {...restProps} opportunity={card.data} />;
        default:
          logger.warn(`Unknown card type: ${card.type}`);
          return null;
      }
    });
  };

  /**
   * Handle closing the card limit alert.
   *
   * This function dismisses the alert shown when the maximum card
   * limit has been reached.
   */
  const handleCloseAlert = () => setShowLimitAlert(false);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} />
          <Box sx={{ color: 'text.secondary' }}>Loading...</Box>
        </Box>
      </ThemeProvider>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  // Main application with routing
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <OpportunityModalProvider
        onAddToLayout={(opp) => {
          const cardId = `opportunity_${opp.opportunity_id || opp.name}`;
          if (cards[cardId]) moveCardToFront(cardId);
          else addCard(cardId, opp, 'opportunity');
        }}
      >
      <ProjectModalProvider
        onAddToLayout={(project) => {
          const cardId = `project_${project.project_id}`;
          if (cards[cardId]) moveCardToFront(cardId);
          else addCard(cardId, project, 'project');
        }}
      >
      <ClientModalProvider
        onAddToLayout={(client) => {
          const cardId = `client_${client.client_id || client.client_name}`;
          if (cards[cardId]) moveCardToFront(cardId);
          else addCard(cardId, client, 'client');
        }}
      >
      <EmployeeModalProvider
        onAddToLayout={(emp) => {
          const cardId = `employee_${emp.employee_id}`;
          if (cards[cardId]) moveCardToFront(cardId);
          else addCard(cardId, emp, 'employee');
        }}
      >
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="100vh"
            flexDirection="column"
            gap={2}
          >
            <CircularProgress size={60} />
            <Box sx={{ color: 'text.secondary' }}>Loading...</Box>
          </Box>
        }>
          <Routes>
            <Route path="/matching" element={
              <MainLayout>
                <MatchingWizard onClose={() => window.history.back()} />
              </MainLayout>
            } />
            
            {/* Main application route */}
            <Route path="/" element={
              <MainLayout chat={<Concierge onContentChange={handleContentChange} cards={cards} onClearGrid={clearGrid} onRestoreCards={restoreCards} />} onLogout={authLogout}>
                {renderCards()}
                
                <Snackbar 
                  open={showLimitAlert} 
                  autoHideDuration={6000} 
                  onClose={handleCloseAlert}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <Alert 
                    onClose={handleCloseAlert} 
                    severity="warning" 
                    sx={{ width: '100%' }}
                  >
                    Maximum card limit (15) reached. Please remove some cards to add more.
                  </Alert>
                </Snackbar>
                
              </MainLayout>
            } />
          </Routes>
        </Suspense>
      </Router>
      </EmployeeModalProvider>
      </ClientModalProvider>
      </ProjectModalProvider>
      </OpportunityModalProvider>
    </ThemeProvider>
  );
}

/**
 * Main App Component - Wraps everything with AuthProvider
 */
function App() {
  return (
    <AuthProvider>
      <AuthContextInitializer />
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </AuthProvider>
  );
}

/**
 * Helper component to initialize API utility with auth context
 */
function AuthContextInitializer() {
  const authContext = useAuth();

  useEffect(() => {
    setAuthContext(authContext);
  }, [authContext]);

  return null;
}

export default App;
